import { useState } from "react";
import {
  Button,
  cn,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@carry/ui";
import { BottomBar } from "../components/BottomBar";
import { Header } from "../components/Header";
import { ReportCard, ReportSectionTitle } from "../components/ReportCard";
import {
  createDriverExpense,
  updateDriverExpense,
  uploadDriverExpenseReceipt,
  useDriverExpenseStopOptions,
  type CreateExpenseInput,
  type DriverExpense,
  type DriverFetcher,
} from "../hooks";
import { jstDateOffset } from "../lib/normalize";
import { enqueueAction } from "../offline-queue";

/** Base UI Select は空文字 value を受け付けないため、未選択はセンチネルで表す (#818)。 */
const NONE = "__none__";

function stopOptionLabel(option: { sequence: number; name: string; completed: boolean }): string {
  return `${option.sequence}. ${option.name}${option.completed ? "" : "（未完了）"}`;
}

export function ExpenseFormScreen({
  fetcher,
  onBack,
  onEnqueue,
  editExpense,
  lockedStop,
  onSaved,
}: {
  fetcher: DriverFetcher;
  onBack: () => void;
  onEnqueue: () => void;
  editExpense?: DriverExpense;
  /**
   * #616: 配送先詳細から開いたときの固定紐付け先。
   * その配達で発生した経費であることが自明なので、発生日と配達先は固定し選び直させない。
   */
  lockedStop?: { routeStopPublicId: string; deliveryDate: string; name: string };
  onSaved?: () => void | Promise<void>;
}) {
  const editing = !!editExpense;
  const [expenseType, setExpenseType] = useState<CreateExpenseInput["expense_type"]>(
    editExpense?.expense_type ?? "highway",
  );
  const [paymentMethod, setPaymentMethod] = useState<CreateExpenseInput["payment_method"]>(
    editExpense?.payment_method ?? "cash",
  );
  const [amount, setAmount] = useState(() => editExpense?.amount.toString() ?? "");
  const [description, setDescription] = useState(() => editExpense?.description ?? "");
  const [incurredOn, setIncurredOn] = useState(
    () => editExpense?.incurred_on ?? lockedStop?.deliveryDate ?? jstDateOffset(0),
  );
  // #616: どの配達先で発生した経費か。任意 (現場で特定できない経費は未選択のまま登録できる)。
  const [routeStopPublicId, setRouteStopPublicId] = useState<string | null>(
    editExpense?.route_stop_public_id ?? lockedStop?.routeStopPublicId ?? null,
  );
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // 配達先が固定されている画面では候補を引く必要がない。
  const stopLocked = lockedStop !== undefined;
  const stopOptions = useDriverExpenseStopOptions({
    apiBaseUrl: fetcher.apiBaseUrl,
    session: fetcher.session,
    fetchImpl: fetcher.fetchImpl,
    date: incurredOn,
    enabled: !stopLocked,
  });
  // 候補の取得前でも選択済みの配達先が空欄に見えないよう、その 1 件だけ補って表示する。
  const selectedMissingFromOptions =
    routeStopPublicId !== null &&
    !stopOptions.options.some((option) => option.route_stop_public_id === routeStopPublicId);

  const handleSubmit = async () => {
    const n = parseInt(amount.replace(/[^\d]/g, ""), 10);
    if (!Number.isFinite(n) || n <= 0) {
      setSubmitError("金額を正しく入力してください");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body: CreateExpenseInput = {
        expense_type: expenseType,
        payment_method: paymentMethod,
        amount: n,
        description: description || undefined,
        incurred_on: incurredOn,
        ...(routeStopPublicId ? { route_stop_public_id: routeStopPublicId } : {}),
      };
      let saved: { public_id: string };
      if (editExpense) {
        saved = await updateDriverExpense(fetcher, editExpense.public_id, {
          expense_type: body.expense_type,
          payment_method: body.payment_method,
          amount: body.amount,
          description: description || null,
          // null を送ると紐付けを解除する。対象日は変えられないため同じ日の配達先のみ選べる。
          route_stop_public_id: routeStopPublicId,
        });
      } else {
        try {
          saved = await createDriverExpense(fetcher, body);
        } catch (e) {
          // オフライン時は経費本体のみキュー化する。領収書画像はキュー化対象外のため、
          // オンライン復帰後に再アップロードが必要 (UI で案内)。
          enqueueAction({
            kind: "create-expense",
            path: "/api/v1/driver/expenses",
            method: "POST",
            body,
          });
          onEnqueue();
          throw e;
        }
      }
      // 領収書があればアップロード (本体作成は成功済みのため、画像失敗は警告に留める)。
      if (receiptFile) {
        let uploadFailed = false;
        try {
          await uploadDriverExpenseReceipt(fetcher, saved.public_id, receiptFile);
        } catch (e) {
          uploadFailed = true;
          setSubmitError(
            `経費は${editing ? "変更" : "記録"}しましたが領収書のアップロードに失敗しました: ${
              e instanceof Error ? e.message : String(e)
            }`,
          );
        }
        // 編集時は同じ画面・ファイルを維持し、ドライバーがそのまま再試行できるようにする。
        if (editing && uploadFailed) return;
      }
      setSubmitted(true);
      if (editing) {
        await onSaved?.();
      } else {
        setAmount("");
        setDescription("");
        setReceiptFile(null);
        // 固定入力 (配送先詳細から) は同じ配達先で続けて登録できるよう紐付けを保つ。
        if (!stopLocked) setRouteStopPublicId(null);
      }
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] pb-[var(--bottom-bar-clearance)]">
      <Header
        title={editing ? "経費を編集" : "経費を追加"}
        sub={incurredOn}
        showBack
        onBack={onBack}
      />
      <div className="px-3 py-3.5">
        <ReportSectionTitle label="発生日" />
        <ReportCard>
          <Input
            type="date"
            value={incurredOn}
            onChange={(e) => {
              setIncurredOn(e.target.value);
              // 日が変われば前の配達先は無効になる (別日の配達先には紐付けられない)。
              setRouteStopPublicId(null);
            }}
            disabled={editing || stopLocked}
            className="num h-auto py-2.5 text-base"
          />
        </ReportCard>

        {/* #616: 高速代・駐車場代は「どの配達で使ったか」が請求・支払の単位になる。 */}
        <ReportSectionTitle
          label="配達先"
          hint={stopLocked ? undefined : "任意"}
          className="mt-3.5"
        />
        <ReportCard>
          <Select
            value={routeStopPublicId ?? NONE}
            disabled={stopLocked}
            onValueChange={(v) => setRouteStopPublicId(!v || v === NONE ? null : v)}
          >
            <SelectTrigger
              aria-label="経費を紐付ける配達先"
              className="h-auto w-full rounded-lg border border-[var(--rule-soft)] bg-[var(--bg-raised)] px-3 py-2.5 text-base text-[var(--ink-2)] shadow-none disabled:opacity-100"
            >
              <SelectValue>
                {(val: string | null) => {
                  if (!val || val === NONE) return "選択しない";
                  if (stopLocked) return lockedStop.name;
                  const option = stopOptions.options.find((o) => o.route_stop_public_id === val);
                  if (option) return stopOptionLabel(option);
                  return editExpense?.stop_name ?? "選択中の配達先";
                }}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {stopLocked ? (
                <SelectItem value={lockedStop.routeStopPublicId}>{lockedStop.name}</SelectItem>
              ) : (
                <SelectItem value={NONE}>選択しない</SelectItem>
              )}
              {!stopLocked && selectedMissingFromOptions && routeStopPublicId && (
                <SelectItem value={routeStopPublicId}>
                  {editExpense?.stop_name ?? "選択中の配達先"}
                </SelectItem>
              )}
              {!stopLocked &&
                stopOptions.options.map((option) => (
                  <SelectItem key={option.route_stop_public_id} value={option.route_stop_public_id}>
                    {stopOptionLabel(option)}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <div className="mt-1.5 text-[length:var(--fs-xs)] text-[var(--ink-3)]">
            {stopLocked
              ? "この配達先の経費として登録します"
              : stopOptions.loading
                ? "配達先を読込中…"
                : stopOptions.error
                  ? `配達先の取得に失敗しました: ${stopOptions.error}`
                  : stopOptions.options.length === 0
                    ? "この日の配達先はありません"
                    : "ETC など後から金額が分かる経費は、発生日を選び直して後日登録できます"}
          </div>
        </ReportCard>

        <ReportSectionTitle label="種別" className="mt-3.5" />
        <ReportCard pad={10}>
          <div className="flex gap-1.5">
            {(
              [
                ["highway", "高速"],
                ["parking", "駐車"],
                ["other", "その他"],
              ] as const
            ).map(([k, l]) => {
              const sel = expenseType === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setExpenseType(k)}
                  aria-pressed={sel}
                  className={cn(
                    "flex-1 cursor-pointer rounded-lg py-2.5 text-[length:var(--fs-sm)] font-medium",
                    sel
                      ? "border-[1.5px] border-[var(--ok)] bg-[var(--st-done-bg)] text-[var(--st-done-ink)]"
                      : "border border-[var(--rule-soft)] bg-[var(--bg-raised)] text-[var(--ink-3)]",
                  )}
                >
                  {l}
                </button>
              );
            })}
          </div>
        </ReportCard>

        <ReportSectionTitle label="支払方法" className="mt-3.5" />
        <ReportCard pad={10}>
          <div className="grid grid-cols-2 gap-1.5">
            {(
              [
                ["cash", "現金"],
                ["credit_card", "カード"],
                ["invoice", "請求書"],
                ["accounts_receivable", "売掛"],
              ] as const
            ).map(([k, l]) => {
              const sel = paymentMethod === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setPaymentMethod(k)}
                  aria-pressed={sel}
                  className={cn(
                    "cursor-pointer rounded-lg py-2.5 text-[length:var(--fs-sm)] font-medium",
                    sel
                      ? "border-[1.5px] border-[var(--ok)] bg-[var(--st-done-bg)] text-[var(--st-done-ink)]"
                      : "border border-[var(--rule-soft)] bg-[var(--bg-raised)] text-[var(--ink-3)]",
                  )}
                >
                  {l}
                </button>
              );
            })}
          </div>
        </ReportCard>

        <ReportSectionTitle label="金額 (円)" className="mt-3.5" />
        <ReportCard>
          <Input
            type="text"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            className="num h-auto py-3 text-right text-[length:var(--fs-2xl)] font-semibold"
          />
        </ReportCard>

        <ReportSectionTitle label="摘要" hint="任意" className="mt-3.5" />
        <ReportCard>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="例: 首都高 3号線"
            className="min-h-[56px] resize-y border-0 bg-transparent p-0 text-base text-[var(--ink-2)] shadow-none focus-visible:ring-0"
          />
        </ReportCard>

        <ReportSectionTitle label="領収書" hint="任意" className="mt-3.5" />
        <ReportCard>
          <label className="flex cursor-pointer flex-col gap-2">
            <input
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              onChange={(e) => setReceiptFile(e.target.files?.[0] ?? null)}
              className="text-[length:var(--fs-sm)] text-[var(--ink-2)] file:mr-2 file:cursor-pointer file:rounded-md file:border file:border-[var(--rule-soft)] file:bg-[var(--bg-raised)] file:px-3 file:py-1.5 file:text-[var(--ink-2)]"
            />
            {receiptFile && (
              <span className="text-[length:var(--fs-xs)] text-[var(--ink-3)]">
                {receiptFile.name}（{Math.round(receiptFile.size / 1024)}KB）
              </span>
            )}
          </label>
        </ReportCard>

        {submitError && (
          <div className="mt-2.5 rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
            <span aria-hidden="true">⚠ </span>
            <span>{submitError}</span>
          </div>
        )}
        {submitted && (
          <div className="mt-2.5 rounded-lg bg-[var(--st-done-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-done-ink)]">
            ✓ 経費を{editing ? "変更" : "記録"}しました
          </div>
        )}
      </div>
      <BottomBar>
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="h-auto min-h-[var(--row-h)] px-3.5 py-3 text-[length:var(--fs-sm)] text-[var(--ink-3)]"
        >
          戻る
        </Button>
        <Button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={submitting}
          className={cn(
            "h-auto min-h-[var(--row-h)] flex-1 bg-[var(--ok)] px-4 py-3 text-[length:var(--fs-md)] font-semibold text-white hover:bg-[var(--ok)]/90",
            submitting && "cursor-wait opacity-70",
          )}
        >
          {submitting ? "送信中…" : editing ? "変更を保存" : "経費を追加"}
        </Button>
      </BottomBar>
    </div>
  );
}
